<template>
  <div class="ccd">
    <div class="header-title">
      <span
        v-if="allowLink"
        class="goBack"
        @click="() => goback()"
      >
        {{ $t('back') }}
      </span>
      <div class="name-wrap">
        <span class="contacts-name">{{ $i18n.t('contacts.title') }}</span>
      </div>
      <div class="btns ccd-action-btns">
        <button
          v-if="!allowLink && allowCreateCompany()"
          type="button"
          class="btn btn--orange action-button"
          role="new-company-button"
          @click="() => navTo('companyCreate')"
        >
          {{ $t('company.newCompany') }}
        </button>
        <button
          v-if="allowLink && allowCreateContact()"
          type="button"
          class="btn btn--orange action-button"
          role="new-contact-button"
          @click="() => navToCreateContact()"
        >
          {{ $t('contact.newContact') }}
        </button>
        <button
          v-if="allowLink && allowLinkContact"
          type="button"
          :class="['float-right btn btn--orange action-button linkbtn', {'link-disabled': penddingToAddContacts.length === 0 }]"
          role="link-button"
          @click="() => linkTo()"
        >
          {{ $t('action.link') }}
        </button>
        <a
          v-if="allowExport && enableTenancyRecordsFeature"
          class="export-emergency-link float-right"
          @click="() => navTo('exportEmergency')"
        >
          {{ $t('settings.security.exportEmergency') }}
        </a>
      </div>
    </div>
    <div
      v-if="isEngineer"
      class="advance-search"
    >
      <span class="float-right btns-group">
        <span class="btns">
          <button
            type="button"
            class="btn btn--darkblue reset"
            role="reset-button"
            @click="() => reset()"
          >{{ $t('action.reset') }}</button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange apply"
            role="search-button"
            @click="() => search()"
          >{{ $t('action.search') }}</button>
        </span>
        <HKLButton
          id="addColumnsBtn"
          class="add-btn"
          color="round"
          action="addColumn"
          @click="() => switchDragableMenu()"
        />
      </span>
      <DraggableMenu
        v-if="showDragableMenu"
        id="draggableMenu"
        :asyncInitDragItems="initTableHeader"
        :userId="userId"
        @update-items="updateRawHeader"
      />
      <ul
        id="advanceSearchForm"
        class="accordion"
        data-accordion
        data-deep-link="true"
        data-allow-all-closed="true"
        data-multi-expand="true"
        data-update-history="true"
        data-deep-link-smudge="500"
      >
        <li
          class="accordion-item"
          data-accordion-item
        >
          <a
            href="advanceSearchFormControl"
            class="accordion-title"
            @click="() => (triangleUp=!triangleUp)"
          >
            {{ $t('contact.advanceSearch') }} <i :class="triangleUp ? 'triangle-up' : 'triangle-down'" />
          </a>
          <div
            id="advanceSearchFormControl"
            class="accordion-content"
            data-tab-content
            @keydown.enter="search"
          >
            <AdvanceSearch
              :searchItems="searchItems"
              :onchange="changeSearchItems"
              :Buildings="Buildings"
              :BuildingFloorZoneMap="BuildingFloorZoneMap"
            />
          </div>
        </li>
      </ul>
    </div>
    <div
      class="search-results-table"
      :class="{ 'search-result-margin-top': !isEngineer }"
    >
      <ResultsTable
        ref="table"
        :enableFilterBar="isEngineer"
        :headerItems="headerData"
        :bodyItems="bodyData"
        :bodyTotal="bodyTotal"
        :searchItems="searchItems"
        :onchange="changeSearchItems"
        :withCheckbox="allowLink"
        :selectTableItems="getLinkInfo"
        :clickItem="clickItem"
        :itemAccess="itemAccess"
        :checkboxCheck="checkboxCheck"
        :keydownEvent="search"
        :hasMore="showLoadMore"
        @loadmore="() => loadMore()"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { computed, unref, onMounted } from 'vue-demi'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { getBuildingFloorZoneMap, getFloorIndex } from '@/api/buildings/request'
import { getAdvanceResults } from '@/api/ccdcontacts/request'
import useRouter from '@/router/useRouter'
import types from '@/store/mutation-types'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { findCreateContactRouteByUserType, findViewContactRouteByUserType } from '@/utils/ccdUtil'
import { ccdRouteNames } from '@/utils/consts'
import EventBus from '@/utils/eventBus'
import { companyType, contractorStatus, nonContractorStatus } from '@/utils/locale'
import navigation, { navToFromCompanyPageWithEdit } from '@/utils/navigation'
import localStore from 'services/localstore'
import { useStoreActions, useStoreGetters } from '@/store/index'
import HKLButton from '../../controls/Button/HKLButton'
import { CCD_EMAIL_LINK_TENANCY_TYPES, CCD_PHONE_LINK_TENANCY_TYPES, TENANCY_ALL_CONTACT_TYPES } from '../ccd/tenancy/consts'
import { useCurrentTrType } from '../ccd/tenancy/hook'
import AdvanceSearch from './AdvanceSearch'
import { booleanColumns, draggableItems, LOAD_MORT_OFFSET, PROPERTY_MAP, roleTypeMap, STATUS_MAP, statusColumns, TableHeaderKey, tenantDraggableItems, vendorDraggableItems } from './Contains'
import DraggableMenu from './DraggableMenu'
import ResultsTable from './ResultsTable'

export default {
  name: 'ContactsIndex',
  components: {
    HKLButton,
    ResultsTable,
    DraggableMenu,
    AdvanceSearch,
  },
  setup () {
    const linkFncQueryNames = TENANCY_ALL_CONTACT_TYPES
    const { route } = useRouter()
    const query = computed(() => unref(route).query)
    const linkType = computed(() => {
      return linkFncQueryNames.find(attr => unref(query)[attr])
    })
    const disabledItemCheckbox = (item) => {
      const needEmail = CCD_EMAIL_LINK_TENANCY_TYPES.some(field => unref(query)[field])
      const needPhone = CCD_PHONE_LINK_TENANCY_TYPES.some(field => unref(query)[field])
      const emailInvalid = needEmail && !item.email
      const phoneInvalid = needPhone && !item.phone
      return emailInvalid || phoneInvalid
    }
    const { isRetailTr } = useCurrentTrType()
    const userStore = useStoreGetters('user', ['isTenant', 'isContractor', 'isHKlandUser', 'currentUser'])
    const { currentUser } = userStore
    const initTableHeader = async () => {
      const headerItemsMap = {
        isTenant: tenantDraggableItems,
        isContractor: vendorDraggableItems,
        isHKlandUser: draggableItems,
      }
      const headerItemsKey = Object.keys(headerItemsMap).find(key => unref(userStore[key]))
      let result = lodash.cloneDeep(headerItemsMap[headerItemsKey] || draggableItems)
      const localHeaderData = await localStore.getItem(unref(currentUser).id + '_' + TableHeaderKey)
      if (!unref(isRetailTr) && unref(userStore.isTenant)) {
        result = result.filter(item => item.name !== 'isMarketing')
      }
      if (localHeaderData && localHeaderData.length) {
        // draggableItem contains `customLabel` function which is not supported by localStore`localHeaderData`, need combine
        result = localHeaderData.reduce((result, item) => {
          const draggableItem = draggableItems.find(
            dragItem => dragItem.title === item.title &&
          dragItem.inputType === item.inputType) || {}
          result.push(Object.assign({}, draggableItem, item))
          return result
        }, [])
      }
      return result
    }
    const { canTenantEditContact, canVenodrCreateUpadteContact, currentTenancy } = useStoreGetters('ccd', ['canTenantEditContact', 'canVenodrCreateUpadteContact', 'currentTenancy'])
    const allowUpdateCompany = computed(() => {
      return aclHelper.getAccess(ccdPermsTypes.contractor.createUpdate) ||
        aclHelper.getAccess(ccdPermsTypes.tenant.createUpdate) ||
        aclHelper.getAccess(ccdPermsTypes.organization.createUpdate)
    })
    const allowLinkContact = computed(() => {
      const queryVal = unref(query)
      if (unref(canTenantEditContact) || unref(canVenodrCreateUpadteContact)) {
        return true
      } else {
        if (queryVal && queryVal.tenancyId) {
          return aclHelper.getAccess(ccdPermsTypes.tenancy.linkContacts) || aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts)
        } else if (queryVal && queryVal.companyId) {
          return unref(allowUpdateCompany)
        }
      }
      return false
    })
    const { loadTenancy } = useStoreActions('ccd', ['loadTenancy'])
    onMounted(async () => {
      // handle reload page case when doing the link Tr contact function
      const queryTid = unref(query).tenancyId
      const nonCurTr = lodash.isEmpty(unref(currentTenancy))
      if (nonCurTr && queryTid) {
        await loadTenancy(unref(query).tenancyId)
      }
    })
    return {
      allowUpdateCompany,
      allowLinkContact,
      isRetailTr,
      query,
      linkFncQueryNames,
      linkType,
      initTableHeader,
      checkboxCheck: disabledItemCheckbox,
    }
  },
  data () {
    return {
      bodyData: [],
      bodyTotal: 0,
      headerData: [],
      Buildings: [],
      BuildingFloorZoneMap: {},
      userId: this.currentUser().id,
      defaultSearchItems: {},
      searchItems: {},
      showDragableMenu: false,
      triangleUp: false,
      page: 0,
      penddingToAddContacts: [],
      allowLink: false,
    }
  },
  computed: {
    ...mapGetters('ccd', ['canTenantEditContact', 'canVenodrCreateUpadteContact', 'searchTotal', 'getSearchItems', 'getSearchResults']),
    ...mapGetters('user', ['isTenant', 'isEngineer', 'isContractor']),
    ...mapGetters('regions', ['enableTenancyRecordsFeature']),
    allowExport () {
      return aclHelper.getAccess(ccdPermsTypes.exportContact.exportEmergency)
    },
    groupContacts () {
      const groups = lodash.groupBy(this.bodyData, 'id')
      return groups
    },
    itemAccess () {
      return {
        tenancy_tradeName: this.allowAccessTenancy,
        company_name: this.allowAccessCompanyDetail,
        company_id: this.allowAccessCompanyDetail,
        contact_name: this.allowAccessContact,
        contact_id: this.allowAccessContact,
      }
    },
    isMarketingOnly () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts) && !aclHelper.getAccess(ccdPermsTypes.tenancy.linkContacts)
    },
    fromRouteNameForCMS () {
      return this.query.from || undefined
    },
    showLoadMore () {
      return this.bodyTotal > this.bodyData.length
    },
  },
  created () {
    document.addEventListener('click', this.clickInPage)
  },
  async mounted () {
    if (this.isEngineer) {
      this.initAccordion()
    }
    const headerData = await this.initTableHeader()
    this.filterHeaderData(headerData)
    if (this.query && (this.query.companyId || this.query.tenancyId)) {
      this.allowLink = true
    }

    if (!this.allowLink) {
      this.bodyData = this.getSearchResults
      this.searchItems = this.getSearchItems
      this.bodyTotal = this.searchTotal
    } else if (this.query && this.query.companyId) {
      this.searchItems = {
        id_company: {
          inputType: 'input',
          value: this.query.companyId,
        },
      }
      await this.search()
    }
    if (Object.keys(this.searchItems).length < 1) {
      this.searchItems = Object.assign({}, this.defaultSearchItems)
    }
    const { BuildingFloorZoneMap, Buildings } = await getBuildingFloorZoneMap()
    this.BuildingFloorZoneMap = BuildingFloorZoneMap
    this.Buildings = Buildings
  },
  destroyed () {
    document.removeEventListener('click', this.clickInPage)
    if (this.isEngineer) {
      this.advanceFormAccordion && this.advanceFormAccordion.destroy()
    }
  },
  methods: {
    ...mapActions('ccd', [
      'linkContactsToCompany',
      'linkContactsToTenancy',
      'loadTenancy',
    ]),
    ...mapGetters('user', [
      'currentUser',
    ]),
    ...mapMutations('ccd', {
      setSearchItems: types.ADVANCE_SEARCH_SET_SEARCH_CONDITIONS,
      setSearchResults: types.ADVANCE_SEARCH_SET_SEARCH_RESULTS,
      setSearchTotal: types.ADVANCE_SEARCH_SET_SEARCH_TOTAL,
    }),
    clickInPage (e) {
      !this.isClickInsideDom('draggableMenu', e) &&
      !this.isClickInsideDom('addColumnsBtn', e) &&
      this.closeDraggableMenu()
    },
    isClickInsideDom (id, e) {
      const ele = document.getElementById(id)
      return ele && ele.contains(e.target)
    },
    updateRawHeader (items) {
      this.filterHeaderData(items)
      this.closeDraggableMenu()
    },
    switchDragableMenu () {
      this.showDragableMenu = !this.showDragableMenu
    },
    closeDraggableMenu () {
      this.showDragableMenu = false
    },
    filterHeaderData (srcData) {
      if (srcData) {
        this.headerData = srcData.filter(item => {
          return item.selected
        })
      }
      if (this.headerData.length > 0) {
        this.defaultSearchItems = this.headerData.reduce((partial, item) => {
          return (!item.defaultValue) ? partial : {
            ...partial,
            [item.style ? item.name + '_' + item.style : item.name]: {
              inputType: item.inputType,
              value: item.defaultValue,
            },
          }
        }, {})
      }
    },
    initAccordion () {
      this.advanceFormAccordion ? this.advanceFormAccordion.destroyed()
        : this.advanceFormAccordion = new Foundation.Accordion($('#advanceSearchForm'))
    },
    reset () {
      this.searchItems = Object.assign({}, this.defaultSearchItems)
    },
    sleep (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    parseVendroSearchResult (data) {
      return data.map(item => {
        return Object.assign({}, item, {
          isAllContact: true,
        })
      })
    },
    dispatchParsedSearchResult (data) {
      if (this.isContractor) {
        return this.parseVendroSearchResult(data)
      } else {
        return data.map(item => {
          const buildingCode = lodash.get(item, 'tenancy.buildingCode')
          const floors = lodash.sortBy(lodash.get(item, 'tenancy.premisesDetailList', []).map(detail => detail.floor), floor => getFloorIndex(buildingCode, floor))
          item.floorIndex = getFloorIndex(buildingCode, floors[0])
          item.floors = floors.join(',')
          return item
        })
      }
    },
    async doSearch () {
      const criteria = this.classifySearchObj()
      const isValidFloors = this.filterFloors(criteria)
      let data = []
      if (isValidFloors) {
        const { data: rawData, total } = await getAdvanceResults(criteria, { raw: true })
        data = this.dispatchParsedSearchResult(rawData)
        this.bodyTotal = total
      } else {
        // It simulates the behavior of clicking the search button, when the user adds region criteria to search
        EventBus.emit('module_loading_startLoading')
        await this.sleep(500)
        EventBus.emit('module_loading_stopLoading')
      }
      return data
    },
    async search () {
      this.page = 0
      const result = await this.doSearch()
      this.$refs.table.scrollToTop()
      this.bodyData = result
      if (!this.allowLink) {
        this.setSearchItems(this.searchItems)
        this.setSearchResults(this.bodyData)
        this.setSearchTotal(this.bodyTotal)
      }
    },
    async loadMore () {
      this.page = this.page + 1
      this.bodyData = this.bodyData.concat(await this.doSearch())
    },
    changeSearchItems (item) {
      const key = Object.keys(item)[0]
      const existingVal = this.searchItems[key]
      const changedItem = item[key]
      if (existingVal && existingVal.value === changedItem.value) {
        return
      } else if (key === 'active_company' && this.searchItems['active_company']) {
        this.searchItems['active_company'].value = ''
      } else if (key === 'buildingCode_tenancy' && changedItem.value.length === 0) {
        if (this.searchItems.floor_tenancy) {
          this.searchItems.floor_tenancy.value = []
        }
        if (this.searchItems.zone_tenancy) {
          this.searchItems.zone_tenancy.value = []
        }
      }

      this.searchItems = Object.assign({}, this.searchItems, item)
    },
    goToEditContacts () {
      const routeOptions = {
        name: this.isTenant ? ccdRouteNames.ccdTenancyTenantView : this.isMarketingOnly ? ccdRouteNames.ccdTenancyView : ccdRouteNames.ccdTenancyEdit,
        params: { id: this.query.tenancyId },
      }
      if (this.isTenant || this.isMarketingOnly) {
        routeOptions.params.enableContactEdit = true
        routeOptions.query = { tabIndex: 1 }
      } else {
        routeOptions.query = { companyId: this.query.companyId, tabIndex: 1 }
        routeOptions.params.tabIndex = 1
        routeOptions.params.afterLinkAction = true
      }

      if (this.linkType) {
        routeOptions.params.type = this.linkType
        routeOptions.params.ccdContactIds = this.penddingToAddContacts.map(item => item.id)
      }
      if (this.isTenant) {
        this.$router.replace(routeOptions)
      } else {
        this.$router.push(routeOptions)
      }
    },
    goback () {
      if (this.query.from) {
        return navToFromCompanyPageWithEdit(this.query)
      }
      if (this.isTenant || this.isMarketingOnly || this.linkType) {
        return this.goToEditContacts()
      }
      this.$router.goBack()
    },
    navTo (name, params, query) {
      navigation.toPage(name, params, query)
    },
    navToCreateContact () {
      const routeName = findCreateContactRouteByUserType()
      this.navTo(routeName, {}, this.query)
    },

    async linkTo () {
      if (this.query && this.query.tenancyId) {
        this.linkToTenancy()
      } else if (this.query && this.query.companyId) {
        this.linkToCompany()
      }
    },
    async linkToTenancy () {
      if (this.penddingToAddContacts.length === 0) {
        return
      }
      this.goToEditContacts()
    },

    async linkToCompany () {
      if (this.penddingToAddContacts.length === 0) {
        return
      }
      // handle duplicated link
      const contactIds = this.penddingToAddContacts.filter(item => {
        const companyIds = (this.groupContacts[item.id] || []).map(contact => contact.company.id)
        return !companyIds.includes(this.query.companyId)
      }).map(item => item.id)
      await this.linkContactsToCompany({
        companyId: this.query.companyId,
        request: contactIds,
      })
      navToFromCompanyPageWithEdit(this.query)
    },
    filterFloors (query) {
      const zones = lodash.get(query, 'tenancyInfo.zone')
      const floors = lodash.get(query, 'tenancyInfo.floor')
      const buildings = lodash.get(query, 'tenancyInfo.building')

      if (zones && zones.length > 0) {
        let validFloors = []
        if (buildings && buildings.length > 0) {
          buildings.forEach(building => {
            zones.forEach(zone => {
              validFloors = lodash.union(validFloors, this.BuildingFloorZoneMap[building].floorMap[zone])
            })
          })
        }
        if (floors && floors.length > 0) {
          validFloors = lodash.intersection(validFloors, floors)
          if (validFloors.length === 0) {
            return false
          }
        }
        deep(query, 'tenancyInfo.floor', validFloors)
      }
      return true
    },
    classifySearchObj () {
      let companyInfo = {}
      let ccdContactInfo = {}
      let tenancyInfo = {}
      const offset = this.page * LOAD_MORT_OFFSET
      const limit = LOAD_MORT_OFFSET
      let companyFilterStatus = null
      for (const key in this.searchItems) {
        const type = key.split('_')
        switch (type[1]) {
          case 'company':
            if (type[0] === 'active') {
              companyFilterStatus = this.searchItems[key]
              break
            }
            this.formatValue(companyInfo, this.searchItems[key], type[0])
            break
          case 'tenancy':
            this.formatValue(tenancyInfo, this.searchItems[key], type[0])
            break
          case 'contact':
          default:
            this.formatValue(ccdContactInfo, this.searchItems[key], type[0])
            break
        }
      }
      if (companyFilterStatus) {
        this.filterCompanySearchCriteria(companyInfo, companyFilterStatus)
      }
      return { companyInfo, ccdContactInfo, tenancyInfo, offset, limit }
    },
    filterCompanySearchCriteria (companyInfo = {}, filteredStatus) {
      if (filteredStatus) {
        if ([contractorStatus.INVITED, contractorStatus.NEW].includes(filteredStatus.value)) {
          // type C companies
          companyInfo.type = [companyType.C]
        }

        if ([nonContractorStatus.ACTIVE, nonContractorStatus.INACTIVE].includes(filteredStatus.value)) {
          // non type C companies
          if (companyInfo.type && companyInfo.type.length) {
            const index = companyInfo.type.indexOf(companyType.C)
            if (index !== -1) {
              companyInfo.type.splice(index, 1)
            }
          } else {
            companyInfo.type = [companyType.T, companyType.O, companyType.D]
          }
        }
        companyInfo.active = STATUS_MAP[filteredStatus.value]
      }
    },

    allowAccessCompany (item) {
      const type = deep(item, 'company.type')
      return (companyType.C === type && aclHelper.getAccess(ccdPermsTypes.contractor.read)) ||
        (companyType.T === type && aclHelper.getAccess(ccdPermsTypes.tenant.read)) ||
        (companyType.O === type && aclHelper.getAccess(ccdPermsTypes.organization.read))
    },
    allowAccessCompanyDetail (item) {
      return this.allowAccessCompany(item)
    },
    allowAccessContact () {
      return this.canTenantEditContact || this.canVenodrCreateUpadteContact || aclHelper.getAccess(ccdPermsTypes.contact.read)
    },
    allowCreateContact () {
      return this.canTenantEditContact || this.canVenodrCreateUpadteContact || aclHelper.getAccess(ccdPermsTypes.contact.createUpdate)
    },
    allowCreateCompany () {
      return aclHelper.getAccess(ccdPermsTypes.organization.createUpdate)
    },
    allowAccessTenancy (item) {
      if (this.isTenant) {
        return deep(item, 'tenancy.hasPerms')
      } else {
        return aclHelper.getAccess(ccdPermsTypes.tenancy.read) &&
          this.currentUser().buildings.some(building => building.code === deep(item, 'tenancy.building'))
      }
    },
    clickItem (item, header) {
      const key = header.style + '_' + header.name
      switch (key) {
        case 'company_id':
        case 'company_name':
          if (this.allowAccessCompany(item)) {
            this.navTo('companyView', { id: lodash.get(item, 'company.id') })
          }
          break
        case 'contact_id':
        case 'contact_name':
          if (this.allowAccessContact()) {
            const routeName = findViewContactRouteByUserType()
            const linkQueryParams = lodash.pick(this.query, [...this.linkFncQueryNames, 'companyId', 'tenancyId'])
            this.navTo(routeName,
              { id: lodash.get(item, 'id') },
              {
                ...linkQueryParams,
                allowLink: this.allowLink && this.allowLinkContact,
                from: this.fromRouteNameForCMS,
              })
          }
          break
        case 'tenancy_tradeName':
          if (this.allowAccessTenancy(item)) {
            this.navTo(this.isTenant ? 'ccdTenancyTenantView' : 'ccdTenancyView',
              { id: lodash.get(item, 'tenancy.id') })
          }
          break
        default:
          break
      }
    },
    formatValue (info, val, key) {
      switch (val.inputType) {
        case 'input':
          info[key] = val.value
          break
        case 'select':
          if (key === 'roleType') {
            this.getRoleTypeValue(info, val)
          } else {
            info[key] = this.getValueFromMap(key, val.value)
          }
          break
        case 'date':
          info[key] = {
            gte: val.value[0] || '',
            lte: val.value[1] ? new Date(moment(val.value[1]).add(1, 'days').valueOf()) : '',
          }
          break
        default:
          break
      }
      return info
    },
    getRoleTypeValue (info, val) {
      if (val.value && val.value.length > 0) {
        val.value.forEach(attr => {
          info[roleTypeMap[attr]] = 1
        })
      }
    },
    getValueFromMap (key, val) {
      let ret = ''
      switch (true) {
        case statusColumns.includes(key):
          ret = STATUS_MAP[val]
          break
        case booleanColumns.includes(key):
          ret = PROPERTY_MAP[val]
          break
        default:
          ret = val
          break
      }
      return ret
    },
    getLinkInfo (contacts) {
      this.penddingToAddContacts = contacts
    },
  },
}
</script>

<style lang="scss" scoped>
.ccd {
  .float-right {
    display: inline-block;
  }
  .goBack {
    position: absolute;
    top: 10px;
    cursor: pointer;
  }
  .goBack:before {
    content: "< ";
  }
  .margin-right10 {
    margin-right: 10px;
  }
  .header-title {
    background: #ebebeb;
    padding: 24px;
    padding-top: 48px;

  .ccd-action-btns {
    margin-top: 23px;
    height: 42px;
  }

  .name-wrap {
    height: 24px;
    line-height: 24px;
      span {
        display: inline-block;
        vertical-align: bottom;
      }
      .contacts-name {
        font-family: Georgia;
        font-size: 30px;
        color: #0C3A58;
      }
      .right-export-link {
        font-family: Verdana;
        font-weight: bold;
        color: #0C3A58;
        font-size: 14px;
        float: right;
        height: 20px;
        line-height: 20px;
      }
    }
    .link-disabled {
      background-color: #d3d3d3;
      color: #fefefe;
      border-bottom: solid 2px #d3d3d3;
    }
    .action-button {
      width: 160px;
      height: 42px;
      color: white;
      font-size: 14px;
    }
    .linkbtn {
      width: 80px;
    }
    .export-emergency-link {
      text-decoration: underline;
      line-height: 42px;
      margin-right: 10px;
    }
  }
  .search-results-table {
    margin: 0 24px;
  }
  html[data-platform="Win32"] {
    .search-results-table {
      margin: 0 7px 0 24px;
    }
  }

  .search-result-margin-top {
    margin-top: 12px;
  }

  .advance-search {
    padding: 0 24px;
    font-size: 18px;
    font-family: Verdana;
    font-weight: bold;
    position: relative;
    .add-btn {
      width: 170px;
      min-height: 35px;
      padding: 0;
    }
    .btns-group {
      height: 59px;
      padding-top: 12px;
    }
    .btns {
      margin-right: 20px;
      .btn {
        height: 37px;
        color: #FFFFFF;
        font-size: 14px;
        display: inline-block;
        text-align: center;
      }
      .reset {
        @extend .btn;
        width: 92px;
        background: #32698E;
        }
      .apply {
        @extend .btn;
        width: 102px;
        margin-left: 20px;
        background: #E69500;
      }
    }
  }
  .reset:hover, .apply:hover, .add-btn:hover {
    cursor: pointer;
  }

  #advanceSearchForm {
    .accordion {
      margin: 0;
    }
    a.accordion-title {
      width: 300px;
      height: 59px;
      border: none;
      font-size: 18px;
      color: #0C3A58;
      text-transform: uppercase;
      &::before {
        content: ''
      }
    }
    a.accordion-title:hover,
    a.accordion-title:focus {
      background: none;
    }

    .accordion-content {
      border: none;
      padding: 0;
    }
  }
  .triangle-up {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 10px solid #0C3A58;
      display: inline-block;
      margin-left: 5px;
  }

  .triangle-down {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 10px solid #0C3A58;
      display: inline-block;
      margin-left: 5px;
  }
}

</style>
